'use client';

import Image from 'next/image';
import { useInView, type MotionValue } from 'motion/react';
import { useRef, useState } from 'react';
import { useLiteMode } from '@/hooks/useLiteMode.hook';
import { useMediaQuery } from '@/hooks/useMediaQuery.hook';
import { MAIN_XL_MAX_QUERY, MAIN_XL_MIN_QUERY } from '@/constants/main';
import poster from '@images/main/main-highlight-poster.webp';
import ScrollHold from './_components/scroll-hold';
import ScrollStage from './_components/scroll-stage';
import { useStoryReached } from './_components/useStoryReached.hook';
import { useVideoScrub } from './_components/useVideoScrub.hook';
import { HighlightContent } from './_components/highlight-content';
import { MobileHighlight } from './_components/highlight-mobile';
import {
  getHighlightActiveCount,
  HIGHLIGHT_ITEMS,
} from './highlight.constants';

function ScrubbedHighlight({
  progress,
  onError,
}: {
  progress: MotionValue<number>;
  onError: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  // 스토리에 들어선 시점부터 받아 둔다. 자기 자리 300px 앞은 폰에서 반 화면도
  // 안 돼, 스크롤해 도착하면 아직 비어 있다.
  const storyReached = useStoryReached();
  const inView = useInView(ref, { margin: '300px', once: true });
  const nearViewport = storyReached || inView;
  const isWide = useMediaQuery(MAIN_XL_MIN_QUERY);
  const [activeCount, setActiveCount] = useState(() =>
    getHighlightActiveCount(0),
  );
  const videoRef = useVideoScrub(progress, nearViewport, video =>
    setActiveCount(getHighlightActiveCount(video.currentTime)),
  );
  return (
    <div ref={ref} className="min-h-[inherit]">
      <HighlightContent
        activeCount={activeCount}
        media={
          <video
            ref={videoRef}
            src={
              nearViewport
                ? `/videos/main-highlight-${isWide ? '1008' : '720'}.mp4`
                : undefined
            }
            poster={poster.src}
            muted
            playsInline
            preload="metadata"
            aria-hidden="true"
            onError={onError}
          />
        }
      />
    </div>
  );
}

export default function HighlightSection() {
  const isLiteMode = useLiteMode();
  const isMobile = useMediaQuery(MAIN_XL_MAX_QUERY);
  const [videoFailed, setVideoFailed] = useState(false);
  return (
    <section className="bg-[#0d0e10] text-white" data-main-highlight>
      {isMobile ? (
        <ScrollHold>
          <MobileHighlight usePoster={isLiteMode} />
        </ScrollHold>
      ) : isLiteMode || videoFailed ? (
        <HighlightContent
          activeCount={HIGHLIGHT_ITEMS.length}
          media={
            <Image
              src={poster}
              alt=""
              sizes={`${MAIN_XL_MIN_QUERY} 504px, 90vw`}
            />
          }
        />
      ) : (
        <ScrollStage
          heightVh={300}
          endHoldPx={400}
          nonStickyEnd="end end"
          // 낮은 노트북 화면(배너 포함)에서 콘텐츠가 제목 위 여백만큼 넘치는 경우에도 고정한다
          maxOverflowPx={40}
        >
          {progress => (
            <ScrubbedHighlight
              progress={progress}
              onError={() => setVideoFailed(true)}
            />
          )}
        </ScrollStage>
      )}
    </section>
  );
}
